import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Building2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const Proveedores = () => {
  const { API, token } = useContext(AuthContext);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [proveedorEditando, setProveedorEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    nif: '',
    direccion: '',
    email: '',
    telefono: ''
  });

  useEffect(() => {
    cargarProveedores();
  }, []);

  const cargarProveedores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/proveedores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProveedores(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalProveedor = (proveedor = null) => {
    if (proveedor) {
      setProveedorEditando(proveedor);
      setFormData({
        nombre: proveedor.nombre || '',
        nif: proveedor.nif || '',
        direccion: proveedor.direccion || '',
        email: proveedor.email || '',
        telefono: proveedor.telefono || ''
      });
    } else {
      setProveedorEditando(null);
      setFormData({
        nombre: '',
        nif: '',
        direccion: '',
        email: '',
        telefono: ''
      });
    }
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setProveedorEditando(null);
    setFormData({
      nombre: '',
      nif: '',
      direccion: '',
      email: '',
      telefono: ''
    });
  };

  const guardarProveedor = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      if (proveedorEditando) {
        await axios.put(
          `${API}/proveedores/${proveedorEditando.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await axios.post(
          `${API}/proveedores`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Proveedor creado exitosamente');
      }
      cerrarModal();
      await cargarProveedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    }
  };

  const eliminarProveedor = async (id) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este proveedor?')) {
      return;
    }

    try {
      await axios.delete(`${API}/proveedores/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proveedor eliminado exitosamente');
      await cargarProveedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar proveedor');
    }
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 size={28} />
            Proveedores
          </h1>
          <p className="text-gray-600 mt-2">
            Gestiona tus proveedores para las facturas recibidas
          </p>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Lista de proveedores registrados
            </p>
            <Button onClick={() => abrirModalProveedor()}>
              <Plus size={16} className="mr-2" />
              Nuevo Proveedor
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : proveedores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay proveedores. Crea uno para empezar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>NIF</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Dirección</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proveedores.map((proveedor) => (
                    <TableRow key={proveedor.id}>
                      <TableCell className="font-medium">{proveedor.nombre}</TableCell>
                      <TableCell>{proveedor.nif || '-'}</TableCell>
                      <TableCell>{proveedor.email || '-'}</TableCell>
                      <TableCell>{proveedor.telefono || '-'}</TableCell>
                      <TableCell>{proveedor.direccion || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirModalProveedor(proveedor)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => eliminarProveedor(proveedor.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Modal de Proveedor */}
        <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {proveedorEditando ? 'Editar' : 'Crear'} Proveedor
              </DialogTitle>
              <DialogDescription>
                {proveedorEditando ? 'Modifica los datos del proveedor' : 'Crea un nuevo proveedor'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre del proveedor"
                />
              </div>
              <div>
                <Label htmlFor="nif">NIF</Label>
                <Input
                  id="nif"
                  value={formData.nif}
                  onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                  placeholder="NIF del proveedor"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="Teléfono"
                />
              </div>
              <div>
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Dirección"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cerrarModal}>
                  Cancelar
                </Button>
                <Button onClick={guardarProveedor}>
                  {proveedorEditando ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Proveedores;


